import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { type Dayjs } from 'dayjs';

// ── Quick-preset bar (top row) ─────────────────────────────────────────

type QuickPreset = { label: string; amount: number; unit: 'day' | 'week' | 'month' };

const QUICK_PRESETS: QuickPreset[] = [
  { label: '1 week', amount: 1, unit: 'week' },
  { label: '15 days', amount: 15, unit: 'day' },
  { label: '1 month', amount: 1, unit: 'month' },
  { label: '3 months', amount: 3, unit: 'month' },
  { label: '6 months', amount: 6, unit: 'month' },
];

// ── Relative-tab rows ──────────────────────────────────────────────────

const RELATIVE_ROWS: { label: string; unit: 'day' | 'week' | 'month'; values: number[] }[] = [
  { label: 'Days', unit: 'day', values: [1, 2, 3, 5, 7, 15] },
  { label: 'Weeks', unit: 'week', values: [1, 2, 4] },
  { label: 'Months', unit: 'month', values: [1, 3, 6] },
];

const UNIT_OPTIONS: { value: 'day' | 'week' | 'month'; label: string }[] = [
  { value: 'day', label: 'Days' },
  { value: 'week', label: 'Weeks' },
  { value: 'month', label: 'Months' },
];

// ── Component ──────────────────────────────────────────────────────────

export interface DateRangeSelectorProps {
  value: DateRange<Dayjs>;
  onChange: (range: DateRange<Dayjs>) => void;
  maxDate?: Dayjs;
}

export default function DateRangeSelector({
  value,
  onChange,
  maxDate,
}: DateRangeSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'absolute' | 'relative'>('relative');

  // Draft state edited inside the popover; committed only on Apply
  const [draftRange, setDraftRange] = useState<DateRange<Dayjs>>(value);
  const [duration, setDuration] = useState(1);
  const [unit, setUnit] = useState<'day' | 'week' | 'month'>('week');
  const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>('1-week');

  // ── Handlers ──

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setDraftRange(value);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleApply = () => {
    if (draftRange[0] && draftRange[1]) {
      onChange(draftRange);
    }
    setOpen(false);
  };

  /** Quick-preset buttons (top bar) — apply immediately and close */
  const handleQuickPreset = (preset: QuickPreset) => {
    const end = dayjs();
    const start = end.subtract(preset.amount, preset.unit);
    onChange([start, end]);
    setOpen(false);
  };

  /** Relative-tab preset buttons — update draft (user must click Apply) */
  const handleRelativePreset = (amount: number, u: 'day' | 'week' | 'month') => {
    const end = dayjs();
    const start = end.subtract(amount, u);
    setDraftRange([start, end]);
    setDuration(amount);
    setUnit(u);
    setSelectedPresetKey(`${amount}-${u}`);
  };

  /** When custom duration or unit changes, update draft immediately */
  const applyCustomDuration = (d: number, u: 'day' | 'week' | 'month') => {
    const end = dayjs();
    const start = end.subtract(d, u);
    setDraftRange([start, end]);
    setSelectedPresetKey(null);
  };

  const handleDurationChange = (val: number) => {
    if (val >= 1 && val <= 9999) {
      setDuration(val);
      applyCustomDuration(val, unit);
    }
  };

  const handleUnitChange = (val: 'day' | 'week' | 'month') => {
    setUnit(val);
    applyCustomDuration(duration, val);
  };

  // ── Display label ──

  const formatDisplay = () => {
    const [start, end] = value;
    if (start && end) {
      return `${start.format('MM/DD/YYYY')}  >  ${end.format('MM/DD/YYYY')}`;
    }
    return 'Select date range';
  };

  // ── Render ──

  return (
    <>
      {/* Trigger — MUI TextField so border matches Region/Type dropdowns */}
      <TextField
        size="small"
        value={formatDisplay()}
        onClick={handleOpen}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarIcon sx={{ fontSize: 18, color: 'action.active' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
            sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
          },
        }}
        sx={{ minWidth: 340, position: 'relative' }}
        helperText="Format: MM/DD/YYYY"
        FormHelperTextProps={{
          sx: { position: 'absolute', bottom: -20, left: 0, m: 0 },
        }}
      />

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { borderRadius: 2, mt: 0.5, minWidth: 540, maxWidth: 680 } },
        }}
      >
        {/* ── Quick-preset top bar ── */}
        <Stack
          direction="row"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {QUICK_PRESETS.map((p) => (
            <Button
              key={p.label}
              size="small"
              onClick={() => handleQuickPreset(p)}
              sx={{
                flex: 1,
                borderRadius: 0,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                py: 1,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {p.label}
            </Button>
          ))}
          <Button
            size="small"
            onClick={() => setTab('absolute')}
            sx={{
              flex: 1,
              borderRadius: 0,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 1,
              color: tab === 'absolute' ? 'primary.main' : 'text.secondary',
              borderBottom: tab === 'absolute' ? '2px solid' : 'none',
              borderColor: 'primary.main',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            Custom
          </Button>
        </Stack>

        {/* ── Absolute / Relative toggle ── */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <ToggleButtonGroup
            value={tab}
            exclusive
            onChange={(_, v) => {
              if (v) setTab(v);
            }}
            size="small"
          >
            <ToggleButton
              value="absolute"
              sx={{ textTransform: 'none', px: 2.5, fontWeight: 600, fontSize: '0.8rem' }}
            >
              Absolute
            </ToggleButton>
            <ToggleButton
              value="relative"
              sx={{ textTransform: 'none', px: 2.5, fontWeight: 600, fontSize: '0.8rem' }}
            >
              Relative
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* ── Tab content ── */}
        <Box sx={{ px: 2, py: 2 }}>
          {tab === 'relative' ? (
            <Stack spacing={2}>
              {/* Preset number grids */}
              {RELATIVE_ROWS.map((row) => (
                <Box key={row.label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.75, display: 'block' }}
                  >
                    {row.label}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {row.values.map((val) => {
                      const isActive = selectedPresetKey === `${val}-${row.unit}`;
                      return (
                        <Button
                          key={val}
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleRelativePreset(val, row.unit)}
                          sx={{
                            minWidth: 48,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            borderRadius: 1,
                          }}
                        >
                          {val}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>
              ))}

              <Divider />

              {/* Custom duration + unit */}
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                  >
                    Duration
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={duration}
                    onChange={(e) => handleDurationChange(parseInt(e.target.value, 10))}
                    slotProps={{ htmlInput: { min: 1, max: 9999 } }}
                    sx={{ width: 100 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
                  >
                    Up to 4 digits.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                  >
                    Unit of time
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={unit}
                    onChange={(e) =>
                      handleUnitChange(e.target.value as 'day' | 'week' | 'month')
                    }
                    sx={{ width: 140 }}
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <MenuItem key={u.value} value={u.value}>
                        {u.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Stack>
            </Stack>
          ) : (
            /* ── Absolute tab: calendar ── */
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <DateRangeCalendar
                  value={draftRange}
                  onChange={(newRange) => setDraftRange(newRange)}
                  maxDate={maxDate}
                  calendars={2}
                />
                {/* Start / End date display */}
                <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                    >
                      Start date
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={draftRange[0]?.format('YYYY/MM/DD') ?? ''}
                      placeholder="YYYY/MM/DD"
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                    >
                      End date
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={draftRange[1]?.format('YYYY/MM/DD') ?? ''}
                      placeholder="YYYY/MM/DD"
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Box>
                </Stack>
              </Box>
            </LocalizationProvider>
          )}
        </Box>

        {/* ── Footer: Cancel + Apply ── */}
        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
          <Button
            size="small"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={!draftRange[0] || !draftRange[1]}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 5,
              px: 3,
            }}
          >
            Apply
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
